import { For, Show, createMemo, createSelector } from "solid-js";

/** One metric row of the Fleet Debrief strip table. */
export interface DebriefMetricDef {
  /** Lowercased daily-training-settings row key (e.g. "twa", "bsp_entry"). */
  key: string;
  label: string;
  unit: string;
  dp: number;
  /**
   * Optional channel-group heading ("Boat States", "Foiling", ...). A spanning header row is
   * emitted the first time each group actually renders a metric. Metrics without a group render
   * exactly as an ungrouped list, which is what the maneuver tables use.
   */
  group?: string;
}

/** Per-boat stats for one metric, resolved from a summary-tables row. */
export interface DebriefBoatStat {
  boat: string;
  color: string;
  mean: number;
  sd: number | null;
  count: number | null;
}

export interface DebriefTooltipPayload {
  boat: string;
  color: string;
  section: string;
  metric: DebriefMetricDef;
  stat: DebriefBoatStat;
}

export interface FleetDebriefStripTableProps {
  title: string;
  /** e.g. "TWS avg 35.1 kph" — derived by the page from the rows' tws_avg. */
  twsLabel?: () => string;
  metrics: DebriefMetricDef[];
  /** Per-boat rows from fetchDailyTrainingSettings (keys lowercased). */
  rows: () => Record<string, unknown>[];
  highlightBoat: () => string;
  showWhiskers: () => boolean;
  getColor: (sourceName: string) => string;
  emptyMessage: string;
  onTipShow: (ev: MouseEvent, tip: DebriefTooltipPayload) => void;
  onTipMove: (ev: MouseEvent) => void;
  onTipHide: () => void;
}

function toFiniteNumber(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : (value as number);
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

export function formatDebriefValue(value: number | null, dp: number): string {
  return value == null ? "—" : value.toFixed(dp);
}

interface StripRowModel {
  metric: DebriefMetricDef;
  /** True when this model is the first rendered one of its group — emit the group header above it. */
  showGroup: boolean;
  stats: DebriefBoatStat[]; // sorted ascending by mean
  fleetMean: number;
  betweenSd: number;
  min: number;
  max: number;
  /** value -> percent [0..100] on the strip */
  x: (v: number) => number;
  /** vertical lane offset (px) per boat for near-coincident dots */
  laneOf: Record<string, number>;
}

/**
 * Which of the metrics that actually rendered should carry a group header above them.
 *
 * Takes the RENDERED metrics, in order, not the declared ones: a metric with no data for any boat
 * produces no row, so the previous rendered metric is not the previous declared metric, and a
 * group whose every metric is empty must not leave a dangling header behind.
 */
export function groupHeaderKeys(renderedMetrics: DebriefMetricDef[]): Set<string> {
  const keys = new Set<string>();
  let lastGroup: string | undefined;
  for (const metric of renderedMetrics) {
    if (metric.group && metric.group !== lastGroup) {
      keys.add(metric.key);
      lastGroup = metric.group;
    }
  }
  return keys;
}

function buildRowModel(
  metric: DebriefMetricDef,
  rows: Record<string, unknown>[],
  getColor: (name: string) => string,
  includeWhiskers: boolean
): StripRowModel | null {
  const stats: DebriefBoatStat[] = [];
  for (const row of rows) {
    const boat = String(row.source_name ?? "").trim();
    const mean = toFiniteNumber(row[metric.key]);
    if (!boat || mean == null) { continue; }
    stats.push({
      boat,
      color: getColor(boat),
      mean,
      sd: toFiniteNumber(row[`${metric.key}_sd`]),
      count: toFiniteNumber(row[`${metric.key}_n`]),
    });
  }
  if (stats.length === 0) { return null; }
  stats.sort((a, b) => a.mean - b.mean);

  const means = stats.map((s) => s.mean);
  const fleetMean = means.reduce((a, v) => a + v, 0) / means.length;
  const betweenSd = means.length > 1
    ? Math.sqrt(means.reduce((a, v) => a + (v - fleetMean) * (v - fleetMean), 0) / (means.length - 1))
    : 0;
  const min = means[0];
  const max = means[means.length - 1];

  let lo = min;
  let hi = max;
  if (includeWhiskers) {
    for (const s of stats) {
      if (s.sd != null) {
        lo = Math.min(lo, s.mean - s.sd);
        hi = Math.max(hi, s.mean + s.sd);
      }
    }
  }
  const pad = (hi - lo || 1) * 0.06;
  lo -= pad;
  hi += pad;
  const x = (v: number) => ((v - lo) / (hi - lo)) * 100;

  const laneOf: Record<string, number> = {};
  let flip = 1;
  stats.forEach((s, i) => {
    laneOf[s.boat] = 0;
    if (i > 0 && x(s.mean) - x(stats[i - 1].mean) < 3.2) {
      laneOf[s.boat] = 6 * flip;
      flip = -flip;
    }
  });

  return { metric, showGroup: false, stats, fleetMean, betweenSd, min, max, x, laneOf };
}

/**
 * Fleet Debrief "strip rows" table: one row per metric — highlighted boat mean +
 * delta-to-fleet chip, fleet mean, and a min→max strip with a dot per boat,
 * optional ±1σ whiskers and a dashed fleet-mean tick.
 */
export default function FleetDebriefStripTable(props: FleetDebriefStripTableProps) {
  const isHighlightBoat = createSelector(() => props.highlightBoat().toLowerCase());

  const rowModels = createMemo((): StripRowModel[] => {
    const rows = props.rows();
    const whiskers = props.showWhiskers();
    const models: StripRowModel[] = [];
    for (const metric of props.metrics) {
      const model = buildRowModel(metric, rows, props.getColor, whiskers);
      if (model) { models.push(model); }
    }
    const headers = groupHeaderKeys(models.map((m) => m.metric));
    for (const model of models) {
      model.showGroup = headers.has(model.metric.key);
    }
    return models;
  });

  const ownStat = (model: StripRowModel): DebriefBoatStat | null =>
    model.stats.find((s) => s.boat.toLowerCase() === props.highlightBoat().toLowerCase()) ?? null;

  const deltaClass = (model: StripRowModel, delta: number): string => {
    if (model.betweenSd > 0 && Math.abs(delta) < model.betweenSd * 0.15) { return "fd-flat"; }
    return delta > 0 ? "fd-above" : "fd-below";
  };

  return (
    <section class="fd-section">
      <div class="fd-section-head">
        <h2>{props.title}</h2>
        <Show when={props.twsLabel && props.twsLabel()}>
          <span class="fd-section-tws">{props.twsLabel!()}</span>
        </Show>
      </div>
      <Show
        when={rowModels().length > 0}
        fallback={<p class="fd-empty">{props.emptyMessage}</p>}
      >
        <div class="fd-tablewrap">
          <table class="fd-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>{props.highlightBoat() || "—"}</th>
                <th>Fleet</th>
                <th>Fleet distribution (min → max)</th>
              </tr>
            </thead>
            <tbody>
              <For each={rowModels()}>
                {(model) => {
                  const own = () => ownStat(model);
                  const delta = () => {
                    const s = own();
                    return s == null ? null : s.mean - model.fleetMean;
                  };
                  return (
                    <>
                    <Show when={model.showGroup}>
                      <tr class="fd-group">
                        <td colSpan={4}>{model.metric.group}</td>
                      </tr>
                    </Show>
                    <tr>
                      <td class="fd-metric">
                        {model.metric.label}
                        <span class="fd-unit">{model.metric.unit}</span>
                      </td>
                      <td class="fd-own">
                        {formatDebriefValue(own()?.mean ?? null, model.metric.dp)}
                        <Show when={delta() != null}>
                          <span class={`fd-delta ${deltaClass(model, delta()!)}`}>
                            {delta()! >= 0 ? "+" : ""}
                            {delta()!.toFixed(model.metric.dp)}
                          </span>
                        </Show>
                      </td>
                      <td class="fd-fleet">{formatDebriefValue(model.fleetMean, model.metric.dp)}</td>
                      <td class="fd-stripcell">
                        <div class="fd-striprow">
                          <span class="fd-slbl">{formatDebriefValue(model.min, model.metric.dp)}</span>
                          <div class="fd-strip">
                            <div class="fd-axis" />
                            <div class="fd-fleettick" style={{ left: `${model.x(model.fleetMean).toFixed(2)}%` }} />
                            <Show when={props.showWhiskers()}>
                              <For each={model.stats}>
                                {(stat) => (
                                  <Show when={stat.sd != null}>
                                    <div
                                      class="fd-whisker"
                                      style={{
                                        left: `${Math.max(0, model.x(stat.mean - stat.sd!)).toFixed(2)}%`,
                                        width: `${(Math.min(100, model.x(stat.mean + stat.sd!)) - Math.max(0, model.x(stat.mean - stat.sd!))).toFixed(2)}%`,
                                        top: `calc(50% - 1.5px + ${model.laneOf[stat.boat]}px)`,
                                        background: stat.color,
                                      }}
                                    />
                                  </Show>
                                )}
                              </For>
                            </Show>
                            <For each={model.stats}>
                              {(stat) => (
                                <div
                                  class={isHighlightBoat(stat.boat.toLowerCase()) ? "fd-dot fd-dot-own" : "fd-dot"}
                                  style={{
                                    left: `${model.x(stat.mean).toFixed(2)}%`,
                                    top: `calc(50% + ${model.laneOf[stat.boat]}px)`,
                                    background: stat.color,
                                  }}
                                  role="img"
                                  aria-label={`${stat.boat} ${stat.mean.toFixed(model.metric.dp)} ${model.metric.unit}`}
                                  onMouseEnter={(ev) =>
                                    props.onTipShow(ev, {
                                      boat: stat.boat,
                                      color: stat.color,
                                      section: props.title,
                                      metric: model.metric,
                                      stat,
                                    })
                                  }
                                  onMouseMove={(ev) => props.onTipMove(ev)}
                                  onMouseLeave={() => props.onTipHide()}
                                />
                              )}
                            </For>
                          </div>
                          <span class="fd-slbl fd-slbl-max">{formatDebriefValue(model.max, model.metric.dp)}</span>
                        </div>
                      </td>
                    </tr>
                    </>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}
